/**
 * The dashboard's landing screen — Task 14.5, replacing a bare session panel and health check with
 * something a staff member actually lands on to decide what to do next.
 *
 * Three real counts from the one workflow this dashboard has: new, unclaimed, and recently resolved
 * support requests. Each is its own request against the existing `/api/admin/support-requests` list
 * endpoint (`useDashboardStats.ts`), so a slow or failed count never blocks the other two — the
 * pattern every stat card here follows is `loading` until its own request answers, not the page's.
 *
 * The health check is not dropped, only demoted: it renders below the stats rather than beside the
 * session panel it used to share top billing with. This screen's primary job is "what needs my
 * attention", and API reachability is a secondary, occasional-glance concern next to it.
 */
import { StatCard, buttonVariants, icons } from '@apuestapr/ui';
import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';

import { useSessionContext } from '../auth/SessionProvider.tsx';
import { HealthSection } from '../health/HealthSection.tsx';
import { supportRoutePath } from '../support/paths.ts';
import { useDashboardStats } from './useDashboardStats.ts';

export const DashboardHome = (): ReactElement => {
  const session = useSessionContext();
  const authenticated = session.state.status === 'authenticated' ? session.state.session : null;
  const stats = useDashboardStats(session.client);
  const hasNew = stats.newRequests.status === 'ready' && (stats.newRequests.value ?? 0) > 0;

  return (
    <section aria-labelledby="inicio">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 id="inicio" className="m-0">
            {authenticated === null
              ? 'Inicio'
              : `Hola, ${authenticated.user.nombre.split(' ')[0] ?? ''}`}
          </h2>
          <p className="m-0 text-muted-foreground">
            Un vistazo a la cola de soporte. Ábrela para trabajar cada solicitud.
          </p>
        </div>
        <Link className={buttonVariants({ variant: 'outline' })} to={supportRoutePath}>
          Ver la cola completa
        </Link>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard
          label="Nuevas"
          value={stats.newRequests.value ?? 0}
          loading={stats.newRequests.status === 'loading'}
          icon={icons.inbox}
          tone={hasNew ? 'attention' : 'neutral'}
          description="Sin revisar todavía"
        />
        <StatCard
          label="Sin asignar"
          value={stats.unassignedRequests.value ?? 0}
          loading={stats.unassignedRequests.status === 'loading'}
          icon={icons.userRound}
          description="Nadie las ha tomado"
        />
        <StatCard
          label="Resueltas"
          value={stats.resolvedRequests.value ?? 0}
          loading={stats.resolvedRequests.status === 'loading'}
          icon={icons.badgeCheck}
          description="En espera de cierre"
        />
      </div>

      <div className="mt-8">
        <HealthSection client={session.client} />
      </div>
    </section>
  );
};
